import { Box, Chip, MenuItem, Stack, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { DataTable } from '../../shared/components/DataTable/DataTable';
import { PageHeader } from '../../shared/components/PageHeader';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { ListQuery } from '../../core/types/api';
import type { AiAssistantRole, AiChatSession, Project, User } from '../../core/types/data-models';

/**
 * Read-only audit log of `AiChatSession` records, across every user in the
 * active tenant. Server-side filterable by project, AI Assistant role, and
 * a title keyword search; sorted by most recent activity first. A row
 * click opens the full message transcript (`AiChatSessionDetailPage`).
 */
export function AiChatMonitoringPage() {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [projectId, setProjectId] = useState<number | null>(null);
  const [assistantRoleKey, setAssistantRoleKey] = useState<string>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce the free-text title search so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Any filter change invalidates the current page — jump back to the first page.
  useEffect(() => {
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [projectId, assistantRoleKey, search]);

  const projectsLookup = useQuery({
    queryKey: ['projects', 'select'],
    queryFn: () => resources.projects.list({ pageSize: 200, sort: 'name' }),
  });
  const projectName = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of projectsLookup.data?.data ?? []) m.set(p.id, p.name);
    return m;
  }, [projectsLookup.data]);

  const rolesLookup = useQuery({
    queryKey: ['aiAssistantRoles', 'select'],
    queryFn: () => resources.aiAssistantRoles.list({ pageSize: 200, sort: 'order' }),
  });
  const roleName = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rolesLookup.data?.data ?? []) m.set(r.key, r.name);
    return m;
  }, [rolesLookup.data]);

  const usersLookup = useQuery({
    queryKey: ['users', 'select'],
    queryFn: () => resources.users.list({ pageSize: 200 }),
  });
  const userLabel = useMemo(() => {
    const m = new Map<number, string>();
    for (const u of usersLookup.data?.data ?? []) m.set(u.id, u.name ?? u.email);
    return m;
  }, [usersLookup.data]);

  const filter: ListQuery['filter'] = useMemo(() => {
    const f: NonNullable<ListQuery['filter']> = {};
    if (projectId != null) f.projectId = projectId;
    if (assistantRoleKey !== 'all') f.assistantRoleKey = assistantRoleKey;
    if (search) f.q = search;
    return f;
  }, [projectId, assistantRoleKey, search]);

  const list = useQuery({
    queryKey: ['aiChatMonitoringSessions', pagination, filter],
    queryFn: () =>
      resources.aiChatMonitoringSessions.list({
        page: pagination.page + 1,
        pageSize: pagination.pageSize,
        sort: '-lastMessageAt',
        filter,
      }),
    placeholderData: (prev) => prev,
  });

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'lastMessageAt',
        headerName: 'Last Activity',
        width: 170,
        renderCell: (p) => {
          const row = p.row as AiChatSession;
          const at = row.lastMessageAt ?? row.createdAt;
          return at ? new Date(at).toLocaleString() : '—';
        },
      },
      {
        field: 'title',
        headerName: 'Title',
        flex: 1,
        minWidth: 220,
        renderCell: (p) => (p.value as string | null) ?? 'Untitled chat',
      },
      {
        field: 'projectId',
        headerName: 'Project',
        width: 180,
        renderCell: (p) => (p.value ? projectName.get(p.value as number) ?? `#${p.value}` : '—'),
      },
      {
        field: 'assistantRoleKey',
        headerName: 'Assistant',
        width: 200,
        renderCell: (p) => {
          const key = p.value as string | null;
          return (
            <Chip
              label={key ? roleName.get(key) ?? key : 'General Assistant'}
              size="small"
              color={key ? 'info' : 'default'}
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'userId',
        headerName: 'User',
        width: 180,
        renderCell: (p) => userLabel.get(p.value as number) ?? `#${p.value}`,
      },
    ],
    [projectName, roleName, userLabel],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title="AI Chat Monitoring"
        description="Audit log of AI Assistant chat sessions across every user. Click a row to view the full message transcript."
      />

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 260 }}>
          <EntitySelect<Project>
            label="Project"
            cacheKey={['projects', 'select']}
            service={resources.projects}
            value={projectId}
            onChange={setProjectId}
          />
        </Box>
        <TextField
          select
          label="Assistant"
          value={assistantRoleKey}
          onChange={(e) => setAssistantRoleKey(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="all">All</MenuItem>
          {(rolesLookup.data?.data ?? []).map((r: AiAssistantRole) => (
            <MenuItem key={r.key} value={r.key}>{r.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Search title"
          placeholder="Keyword in session title"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 240 }}
        />
      </Stack>

      <DataTable<AiChatSession>
        rows={list.data?.data ?? []}
        columns={columns}
        loading={list.isFetching}
        rowCount={list.data?.page.total ?? 0}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        errorMessage={list.isError ? extractErrorMessage(list.error) : undefined}
        onRowClick={(row) => navigate(`/ai-chat-monitoring/${row.id}`)}
      />
    </Box>
  );
}
